import { useEvent } from '@shared-core/hooks/use-event'
import { ActiveTouchAction, type ActiveTouchActionProps } from '@shared-ui/components/ui/active-touch-action'
import { AppIcon, type AppIconSourcesExtended } from '@shared-ui/components/ui/app-icon'
import { Loader } from '@shared-ui/components/ui/loader'
import { GText } from '@shared-ui/components/ui/text'
import type { FontWeight } from '@shared-ui/components/ui/text/types'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { handleOpenLink } from '@shared-ui/platform/link'
import { useAppTheme } from '@shared-ui/theme'
import React, { memo } from 'react'
import { type StyleProp, type TextStyle, View, type ViewStyle } from 'react-native'
import { useCreateStyles } from './styles'

type Themes = 'black' | 'gray' | 'white' | 'primary-light' | 'secondary-light' | 'danger' | 'inverted'
type Sizes = 'small' | 'medium'

export type ButtonProps = ActiveTouchActionProps & {
  iconLeft?: AppIconSourcesExtended
  iconRight?: AppIconSourcesExtended
  theme?: Themes
  size?: Sizes
  href?: string
  onPress?: () => void
  disabled?: boolean
  isLoading?: boolean
  testID?: string
}

export const Button = memo(function ButtonComponent({
  style,
  children,
  onPress,
  href,
  disabled,
  testID,
  isLoading,
  iconLeft,
  iconRight,
  ...rest
}: ButtonProps) {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const haptic = useHaptic()

  const handlePress = useEvent(() => {
    // Если сейчас идет загрузка, то не обрабатываем нажатие
    if (isLoading) {
      return
    }

    if (onPress) {
      haptic('impactLight')

      onPress()
    }

    // обработка открытия ссылки через такую функцию. Сразу вызываем
    if (href) {
      void handleOpenLink(href)()
    }
  })

  const stylesWrapper: Array<StyleProp<ViewStyle> | undefined> = [styles.button]
  const stylesText: Array<StyleProp<TextStyle> | undefined> = [styles.text]
  let loaderColor = theme.COLOR_TEXT_INVERSE_PRIMARY

  if (rest.theme === 'gray') {
    stylesWrapper.push(styles.grayButton)
    stylesText.push(styles.grayButtonText)
    loaderColor = theme.COLOR_TEXT_PRIMARY
  }

  if (rest.theme === 'white') {
    stylesWrapper.push(styles.whiteButton)
    stylesText.push(styles.whiteButtonText)
    loaderColor = theme.COLOR_NEUTRAL_950
  }

  if (rest.theme === 'black') {
    stylesWrapper.push(styles.blackButton)
    stylesText.push(styles.blackButtonText)
  }

  if (rest.theme === 'inverted') {
    stylesWrapper.push(styles.invertedButton)
    stylesText.push(styles.invertedButtonText)
    loaderColor = theme.COLOR_TEXT_INVERSE_PRIMARY
  }

  if (rest.theme === 'danger') {
    stylesWrapper.push(styles.dangerButton)
    stylesText.push(styles.dangerButtonText)
    loaderColor = theme.COLOR_TEXT_FIXED
  }

  if (rest.theme === 'primary-light') {
    stylesWrapper.push(styles.primaryLightButton)
    stylesText.push(styles.primaryLightButtonText)
    loaderColor = theme.COLOR_TEXT_BRAND
  }

  if (rest.theme === 'secondary-light') {
    stylesWrapper.push(styles.primaryLightButton)
    stylesText.push(styles.primaryLightButtonSecondary)
    loaderColor = theme.COLOR_TEXT_PRIMARY
  }

  if (rest.size === 'small') {
    stylesWrapper.push(styles.buttonSmall)
    stylesText.push(styles.buttonSmallText)
  }

  if (rest.size === 'medium') {
    stylesWrapper.push(styles.buttonMedium)
    stylesText.push(styles.buttonMediumText)
  }

  if (!rest.theme) {
    stylesWrapper.push(styles.primaryButton)
    stylesText.push(styles.primaryButtonText)
  }

  if (disabled) {
    stylesWrapper.push(styles.disabled)
    stylesText.push(styles.disabledText)
    loaderColor = theme.COLOR_TEXT_DISABLED
  }

  if (style) {
    stylesWrapper.push(style)
  }

  let textSize = 16
  let textFw: FontWeight = 600

  if (rest.size === 'small' || rest.size === 'medium') {
    textSize = 14
    textFw = 600
  }

  let iconSize = 24

  if (rest.size === 'medium') iconSize = 20

  if (rest.size === 'small') iconSize = 16

  const renderChild = (child: React.ReactNode) => {
    if (typeof child === 'string') {
      return (
        <GText size={textSize} fw={textFw} lh={textSize === 16 ? 20 : 18.2} style={stylesText}>
          {child}
        </GText>
      )
    }
    return child
  }

  return (
    <ActiveTouchAction onPress={handlePress} disabled={disabled} testID={testID} isPressHapticDisabled>
      <View style={stylesWrapper}>
        {isLoading && <Loader size={32} color={loaderColor} />}
        {!isLoading && iconLeft && <AppIcon icon={iconLeft} size={iconSize} color={loaderColor} />}
        {!isLoading && React.Children.map(children, renderChild)}
        {!isLoading && iconRight && <AppIcon icon={iconRight} size={iconSize} color={loaderColor} />}
      </View>
    </ActiveTouchAction>
  )
})
